/**
 * Quick-view preload cache: begrens de grootte én de inhoud.
 *
 * `QuickView.requestProductFromUrl` in bundle.js doet:
 *
 *     this.cache[url] = fetch(url).then(r => r.text()).then(parseHTML)
 *
 * `parseHTML` is `new DOMParser().parseFromString(html, 'text/html')` — een
 * compleet Document van de héle PDP (header, footer, alle secties). Dat
 * Document blijft voor de rest van het paginabezoek in `this.cache` staan;
 * er is geen eviction. En `callQuickView(false)` — de hover-preload op elke
 * product-card — vult diezelfde cache, dus je hoeft niet eens te klikken.
 *
 * Gemeten op /collections/all (headless Chrome, GC geforceerd vóór elke
 * meting): puur met de muis over 15 cards bewegen, nul kliks:
 *
 *     baseline                     nodes= 14.324  documents= 6
 *     na 15 cards hoveren          nodes=127.057  documents=36
 *     na `quickView.cache = {}`    nodes= 14.576  documents= 6
 *
 * Ruim 112.000 DOM-nodes en 30 Documents vastgehouden door de cache alleen.
 * Op desktop merk je dat als traagheid; WebKit op iOS kwakt de tab om
 * ("Problemen met deze pagina") — hetzelfde OOM-patroon dat eerder de
 * inline variant-picker-scripts opleverde.
 *
 * Deze patch verandert twee dingen aan de cache-entry:
 *
 * 1. We bewaren alleen het stuk dat de quick-view daadwerkelijk gebruikt.
 *    `setProductFromHTML` doet niets anders dan `$el('#' + targetId, html)`,
 *    dus alles behalve dat ene element gaat direct na het parsen uit het
 *    Document. Header, footer, reviews en de overige secties — het leeuwendeel
 *    van een PDP van ~1,3 MB — zijn daarmee weg vóór de entry in de cache
 *    belandt.
 *
 * 2. Maximaal MAX_ENTRIES entries, LRU. De preload is er om de kaart die je
 *    op het punt staat aan te klikken snel te laten openen; een paar entries
 *    dekt dat volledig. Valt er een entry uit en klik je hem alsnog aan, dan
 *    haalt `requestProductFromUrl` hem opnieuw op — uit de HTTP-cache, dus
 *    zonder merkbare vertraging.
 *
 * De patch zit op het prototype (zelfde aanpak als product-card-hover-perf.js)
 * zodat bundle.js zelf onaangeroerd blijft bij een thema-update.
 */
(function () {
  'use strict';

  /* Vijf entries dekt "een paar cards langsgaan en er dan één openen" ruim.
     Hoger kost lineair meer geheugen zonder dat je er snelheid voor
     terugkrijgt: een miss vangt de HTTP-cache al op. */
  var MAX_ENTRIES = 5;

  function patch(QuickViewClass) {
    if (!QuickViewClass || !QuickViewClass.prototype) return;
    var proto = QuickViewClass.prototype;
    if (proto.__cnBoundedCache) return; // idempotent
    if (typeof proto.requestProductFromUrl !== 'function') return;
    proto.__cnBoundedCache = true;

    /* Insertie-volgorde van de urls in this.cache, oudste eerst. Per
       instance, want in theorie kunnen er meerdere quick-views zijn. */
    var order = new WeakMap();

    function touch(instance, url) {
      var urls = order.get(instance);
      if (!urls) { urls = []; order.set(instance, urls); }
      var at = urls.indexOf(url);
      if (at !== -1) urls.splice(at, 1);
      urls.push(url);
      while (urls.length > MAX_ENTRIES) {
        var evicted = urls.shift();
        delete instance.cache[evicted];
      }
    }

    proto.requestProductFromUrl = function (url, id) {
      if (this.cache[url]) {
        touch(this, url); // hit → jongste maken, niet opnieuw ophalen
        return;
      }

      /* Pre-rendered quick-view in de sectie zelf ([data-quick-view-product-id]).
         Dit thema rendert die markup niet, maar de originele code kent het pad
         en `resetProduct()` leunt erop — dus intact laten. */
      this.rendered = document.querySelector(
        '.shopify-section [data-quick-view-product-id="' + id + '"]'
      );
      if (this.rendered && this.rendered.dataset.quickViewProductUrl === url) {
        var self = this;
        this.cache[url] = Promise.resolve().then(function () { return self.rendered; });
        touch(this, url);
        return;
      }

      var targetId = this.dataset.targetId;
      this.cache[url] = fetch(url)
        .then(function (response) { return response.text(); })
        .then(function (responseText) {
          var doc = new DOMParser().parseFromString(responseText, 'text/html');
          var target = targetId ? doc.getElementById(targetId) : null;
          if (target) {
            /* Alles behalve het target uit het Document gooien. We geven
               bewust het Document terug in plaats van een los element: dan
               blijft `setProductFromHTML` — `$el('#' + targetId, html)` +
               `target.cloneNode(true)` — exact hetzelfde werk doen, met
               dezelfde owner-document semantiek als voorheen. Alleen de
               header, footer, reviews en overige secties zijn weg. */
            doc.head.replaceChildren();
            doc.body.replaceChildren(target);
          }
          return doc;
        })
        .catch(function () {
          console.log('error in quick view');
        });
      touch(this, url);
    };
  }

  if (!('customElements' in window)) return;
  customElements.whenDefined('quick-view').then(function () {
    patch(customElements.get('quick-view'));
  }).catch(function () { /* thema zonder quick-view → skip */ });
})();
